import { useState, useEffect, useCallback } from 'react';
import { Plus, Pencil, Trash2, Star } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const emptyTestimonial = { name: '', location: 'Christchurch, NZ', text: '', rating: 5, status: 'active' };

export default function AdminTestimonials() {
  const [testimonials, setTestimonials] = useState([]);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyTestimonial);
  const [dialogOpen, setDialogOpen] = useState(false);

  const fetchTestimonials = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/admin/testimonials`);
      setTestimonials(data);
    } catch (err) {
      console.error('Failed to load testimonials:', err);
      toast.error('Failed to load testimonials');
    }
  }, []);
  useEffect(() => { fetchTestimonials(); }, [fetchTestimonials]);

  const openNew = () => { setEditing(null); setForm(emptyTestimonial); setDialogOpen(true); };
  const openEdit = (t) => {
    setEditing(t.id);
    setForm({ name: t.name, location: t.location || '', text: t.text, rating: t.rating || 5, status: t.status || 'active' });
    setDialogOpen(true);
  };

  const handleSave = async () => {
    try {
      if (editing) {
        await axios.put(`${API}/admin/testimonials/${editing}`, form);
        toast.success('Testimonial updated');
      } else {
        await axios.post(`${API}/admin/testimonials`, form);
        toast.success('Testimonial created');
      }
      setDialogOpen(false);
      fetchTestimonials();
    } catch (err) {
      console.error('Failed to save testimonial:', err);
      toast.error('Failed to save');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this testimonial?')) return;
    try {
      await axios.delete(`${API}/admin/testimonials/${id}`);
      toast.success('Deleted');
      fetchTestimonials();
    } catch (err) {
      console.error('Failed to delete testimonial:', err);
      toast.error('Failed');
    }
  };

  return (
    <div data-testid="admin-testimonials">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold" style={{ fontFamily: "'Poppins', sans-serif" }}>Testimonials</h1>
        <Button onClick={openNew} className="btn-gold text-sm" data-testid="add-testimonial-btn"><Plus size={16} className="mr-2" />Add Testimonial</Button>
      </div>
      <div className="bg-white rounded-sm border overflow-auto">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Name</TableHead>
              <TableHead>Review</TableHead>
              <TableHead>Rating</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {testimonials.map(t => (
              <TableRow key={t.id}>
                <TableCell className="font-medium">{t.name}<br/><span className="text-xs text-gray-400">{t.location}</span></TableCell>
                <TableCell className="text-sm max-w-xs truncate">{t.text}</TableCell>
                <TableCell><span className="text-[#D4AF37]">{'★'.repeat(t.rating || 5)}</span></TableCell>
                <TableCell><span className={`text-xs px-2 py-1 rounded ${t.status === 'active' ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>{t.status}</span></TableCell>
                <TableCell>
                  <div className="flex gap-2">
                    <button onClick={() => openEdit(t)} className="p-1 hover:text-[#D4AF37]"><Pencil size={16} /></button>
                    <button onClick={() => handleDelete(t.id)} className="p-1 hover:text-red-500"><Trash2 size={16} /></button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-md" data-testid="testimonial-dialog">
          <DialogHeader><DialogTitle style={{ fontFamily: "'Poppins', sans-serif" }}>{editing ? 'Edit Testimonial' : 'New Testimonial'}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div><Label>Name *</Label><Input value={form.name} onChange={e => setForm({...form, name: e.target.value})} placeholder="e.g. N. Ryan" data-testid="testimonial-name" /></div>
            <div><Label>Location</Label><Input value={form.location} onChange={e => setForm({...form, location: e.target.value})} placeholder="Christchurch, NZ" /></div>
            <div><Label>Review Text *</Label><Textarea value={form.text} onChange={e => setForm({...form, text: e.target.value})} rows={4} data-testid="testimonial-text" /></div>
            <div className="grid grid-cols-2 gap-4">
              <div><Label>Rating</Label>
                <Select value={String(form.rating)} onValueChange={v => setForm({...form, rating: parseInt(v)})}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="5">5 Stars</SelectItem>
                    <SelectItem value="4">4 Stars</SelectItem>
                    <SelectItem value="3">3 Stars</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div><Label>Status</Label>
                <Select value={form.status} onValueChange={v => setForm({...form, status: v})}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="active">Active</SelectItem>
                    <SelectItem value="inactive">Inactive</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <Button onClick={handleSave} className="btn-gold w-full" data-testid="save-testimonial-btn">Save Testimonial</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
